// Register the custom element
if (!customElements.get('custom-scrollbar')) {
class CustomScrollbar extends HTMLElement {
  constructor() {
    super();
    this.isDragging = false;
    this.startX = 0;
    this.startLeft = 0;
    this.scrollContainer = null;
    this.track = null;
    this.thumb = null;
  }

  connectedCallback() {
    this.track = this.querySelector('.custom-scrollbar__track');
    this.thumb = this.querySelector('.custom-scrollbar__thumb');
    
    if (!this.track || !this.thumb) {
      return;
    }
    
    // Find the scrollable container
    const targetSelector = this.dataset.targetSelector;
    
    if (targetSelector) {
      const sectionSpacing = this.closest('.section-spacing');
      
      if (sectionSpacing) {
        this.scrollContainer = sectionSpacing.querySelector(targetSelector);
      }
    }
    
    if (!this.scrollContainer) {
      return;
    }
    
    this.setupEventListeners();
    this.updateScrollbar();
    
    // Initial update and periodic check for content changes
    setTimeout(() => this.updateScrollbar(), 100);
    setTimeout(() => this.updateScrollbar(), 500);
    
    // Set up periodic scroll position monitoring
    this.lastScrollLeft = this.scrollContainer.scrollLeft;
    this.scrollMonitor = setInterval(() => {
      const currentScrollLeft = this.scrollContainer.scrollLeft;
      if (currentScrollLeft !== this.lastScrollLeft) {
        this.lastScrollLeft = currentScrollLeft;
        this.updateScrollbar();
      }
    }, 50); // Check every 50ms
  }

  setupEventListeners() {
    // Track click events
    this.track.addEventListener('click', (e) => {
      this.handleTrackClick(e);
    });
    
    // Thumb drag events
    this.thumb.addEventListener('mousedown', (e) => {
      this.startDragging(e);
    });
    this.thumb.addEventListener('touchstart', (e) => {
      this.startDragging(e);
    });
    
    // Global mouse/touch events
    document.addEventListener('mousemove', (e) => this.drag(e));
    document.addEventListener('touchmove', (e) => this.drag(e));
    document.addEventListener('mouseup', () => {
      this.stopDragging();
    });
    document.addEventListener('touchend', () => {
      this.stopDragging();
    });
    
    // Scroll container events
    this.scrollContainer.addEventListener('scroll', () => {
      this.updateScrollbar();
    });
    
    // Also listen for touch events on the scroll container
    this.scrollContainer.addEventListener('touchmove', () => {
      // Use requestAnimationFrame to update after the scroll
      requestAnimationFrame(() => {
        this.updateScrollbar();
      });
    });
    
    // Listen for wheel events
    this.scrollContainer.addEventListener('wheel', () => {
      requestAnimationFrame(() => {
        this.updateScrollbar();
      });
    });
    
    // Listen for touch events on the entire document to catch any scrolling
    document.addEventListener('touchmove', (e) => {
      if (e.target.closest('.media-grid--inner.swipe-on-mobile')) {
        requestAnimationFrame(() => {
          this.updateScrollbar();
        });
      }
    });
    
    // Listen for scroll events on the specific scroll container
    if (this.scrollContainer) {
      this.scrollContainer.addEventListener('scroll', () => {
        requestAnimationFrame(() => {
          this.updateScrollbar();
        });
      });
    }
    
    // Window resize
    window.addEventListener('resize', () => {
      this.updateScrollbar();
    });
  }

  handleTrackClick(e) {
    if (this.isDragging) {
      return;
    }
    
    const rect = this.track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const trackWidth = rect.width;
    const scrollRatio = clickX / trackWidth;
    
    const scrollWidth = this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;
    const newScrollLeft = scrollRatio * scrollWidth;
    
    // Smooth scroll with easing
    this.smoothScrollTo(newScrollLeft);
  }

  startDragging(e) {
    this.isDragging = true;
    this.startX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
    this.startLeft = this.thumb.offsetLeft;
    
    e.preventDefault();
  }

  drag(e) {
    if (!this.isDragging) return;
    
    const currentX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    const deltaX = currentX - this.startX;
    const trackWidth = this.track.offsetWidth;
    const thumbWidth = this.thumb.offsetWidth;
    const maxLeft = trackWidth - thumbWidth;
    
    let newLeft = this.startLeft + deltaX;
    newLeft = Math.max(0, Math.min(newLeft, maxLeft));
    
    this.thumb.style.left = newLeft + 'px';
    
    // Update scroll position with smooth interpolation
    const scrollRatio = newLeft / maxLeft;
    const scrollWidth = this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;
    const newScrollLeft = scrollRatio * scrollWidth;
    
    // Smooth scroll during drag
    this.smoothScrollTo(newScrollLeft, true);
  }

  stopDragging() {
    this.isDragging = false;
  }

  // Easing function for smooth scrolling
  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  // Smooth scroll with easing
  smoothScrollTo(targetScrollLeft, immediate = false) {
    const startScrollLeft = this.scrollContainer.scrollLeft;
    const distance = targetScrollLeft - startScrollLeft;
    
    if (Math.abs(distance) < 1) {
      this.scrollContainer.scrollLeft = targetScrollLeft;
      return;
    }

    if (immediate) {
      // For drag operations, use immediate smooth scrolling
      this.scrollContainer.scrollLeft = targetScrollLeft;
      return;
    }

    const duration = 300; // 300ms for smooth animation
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Apply easing
      const easedProgress = this.easeOutCubic(progress);
      const currentScrollLeft = startScrollLeft + (distance * easedProgress);
      
      this.scrollContainer.scrollLeft = currentScrollLeft;
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  disconnectedCallback() {
    if (this.scrollMonitor) {
      clearInterval(this.scrollMonitor);
    }
  }

  updateScrollbar() {
    
    if (!this.scrollContainer) {
      console.warn('CustomScrollbar: No scroll container in updateScrollbar');
      return;
    }
    
    const scrollLeft = this.scrollContainer.scrollLeft;
    const scrollWidth = this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;
    const trackWidth = this.track.offsetWidth;
    
    
    // Hide scrollbar if no scrolling is needed
    if (scrollWidth <= 0) {
      this.style.display = 'none';
      return;
    }
    
    this.style.display = 'block';
    
    // Set thumb width to fixed 96px
    const thumbWidth = 96;
    this.thumb.style.width = thumbWidth + 'px';
    
    // Calculate thumb position
    const scrollRatio = scrollLeft / scrollWidth;
    const maxLeft = trackWidth - thumbWidth;
    const newLeft = Math.max(0, Math.min(scrollRatio * maxLeft, maxLeft));
    
    this.thumb.style.left = newLeft + 'px';
    
  }

  disconnectedCallback() {
    // Clean up event listeners when component is removed
    if (this.scrollContainer) {
      // Remove the scroll listener (we need to store the bound function to remove it)
      // For now, we'll let garbage collection handle it since the element is being removed
    }
    
    // Clear any running intervals
    if (this.scrollMonitor) {
      clearInterval(this.scrollMonitor);
    }
  }
}

  customElements.define('custom-scrollbar', CustomScrollbar);
}